import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Pagination from '@mui/material/Pagination';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import { newsApi } from '../services/api';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function News() {
  const navigate = useNavigate();
  const [newsList, setNewsList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentCategory, setCurrentCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // 加载分类
  useEffect(() => {
    newsApi.getCategories().then((data) => {
      if (Array.isArray(data)) setCategories(data);
    }).catch(() => {});
  }, []);

  // 加载列表
  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 12 };
    if (currentCategory !== 'all') params.category = currentCategory;

    newsApi
      .getList(params)
      .then((data) => {
        if (Array.isArray(data)) {
          setNewsList(data);
          setTotalPages(1);
        } else if (data && data.news) {
          setNewsList(data.news);
          setTotalPages(data.pagination?.pages || 1);
        } else {
          setNewsList([]);
        }
      })
      .catch(() => setNewsList([]))
      .finally(() => setLoading(false));
  }, [page, currentCategory]);

  const handleCategoryChange = (_, value) => {
    setCurrentCategory(value);
    setPage(1);
  };

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 5 }}>
          <Typography variant="h2" sx={{ mb: 1 }}>新闻资讯</Typography>
          <Typography variant="body1" color="text.secondary">
            了解象棋圈最新动态
          </Typography>
        </Box>

        {/* 分类筛选 */}
        {categories.length > 0 && (
          <Tabs
            value={currentCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="全部" value="all" />
            {categories.map((cat) => (
              <Tab key={cat._id} label={cat.name} value={cat._id} />
            ))}
          </Tabs>
        )}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : newsList.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography color="text.secondary">暂无资讯</Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 3,
              }}
            >
              {newsList.map((news) => (
                <Card
                  key={news._id}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
                  }}
                  onClick={() => navigate(`/news/${news._id}`)}
                >
                  {news.coverImageInfo?.url && (
                    <CardMedia
                      component="img"
                      height={180}
                      image={news.coverImageInfo.url}
                      alt={news.title}
                      sx={{ objectFit: 'cover' }}
                    />
                  )}
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: '1.05rem',
                        mb: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.5,
                      }}
                    >
                      {news.title}
                    </Typography>
                    {news.summary && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.7,
                          mb: 1.5,
                        }}
                      >
                        {news.summary}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled">
                      {formatDate(news.publishedAt || news.createdAt)}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}
